import { type FormEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/lib/api";
import {
  type CompanySummary,
  useCompanies,
  useCreateCompany,
  useDeleteCompany,
  useEnterCompany,
  useSuspendCompany,
  useUnsuspendCompany,
} from "@/lib/admin";
import { useCurrentUser, useLogout } from "@/lib/auth";
import { formatDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { HelpSection } from "@/components/HelpSection";

type StatusFilter = "all" | "active" | "pending_suspension" | "suspended";

export function CompaniesPage() {
  const { t, i18n } = useTranslation();
  const { data: me } = useCurrentUser();
  const logout = useLogout();
  const companies = useCompanies();
  const [showCreate, setShowCreate] = useState(false);
  const [inviteResult, setInviteResult] = useState<{ url: string; expires: string } | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    if (!companies.data) return [];
    const q = search.trim().toLowerCase();
    return companies.data.filter((c) => {
      if (statusFilter !== "all" && c.status !== statusFilter) return false;
      if (!q) return true;
      return (
        c.name.toLowerCase().includes(q) ||
        c.slug.toLowerCase().includes(q) ||
        (c.notes?.toLowerCase().includes(q) ?? false)
      );
    });
  }, [companies.data, search, statusFilter]);

  const counts = useMemo(() => {
    const all = companies.data ?? [];
    return {
      all: all.length,
      active: all.filter((c) => c.status === "active").length,
      pending_suspension: all.filter((c) => c.status === "pending_suspension").length,
      suspended: all.filter((c) => c.status === "suspended").length,
    };
  }, [companies.data]);

  return (
    <main className="min-h-dvh bg-bg-primary text-fg-primary">
      <header className="border-b border-border bg-bg-secondary">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-x-4 gap-y-3 px-6 py-4">
          <div className="min-w-0">
            <h1 className="text-lg font-semibold">{t("admin.title")}</h1>
            <p className="text-xs text-fg-muted">{t("admin.subtitle")}</p>
          </div>
          <div className="flex min-w-0 items-center gap-4">
            <button
              type="button"
              onClick={() =>
                void i18n.changeLanguage(i18n.language === "ru" ? "en" : "ru")
              }
              className="text-xs text-fg-muted hover:text-fg-primary"
              aria-label={t("app.toggle_language")}
            >
              {i18n.language === "ru" ? "EN" : "RU"}
            </button>
            <span className="min-w-0 truncate text-sm text-fg-secondary">
              {me?.username}{" "}
              <span className="text-fg-muted">({t(`role.${me?.role ?? "superadmin"}`)})</span>
            </span>
            <Button variant="secondary" size="sm" onClick={() => logout.mutate()}>
              {t("dashboard.logout")}
            </Button>
          </div>
        </div>
      </header>

      <section className="mx-auto max-w-6xl px-6 py-8 space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-2xl font-semibold">{t("admin.companies.title")}</h2>
          <Button
            className="bg-accent-superadmin hover:opacity-90"
            onClick={() => setShowCreate(true)}
          >
            {t("admin.companies.create")}
          </Button>
        </div>

        {/* Filter bar */}
        {companies.data && companies.data.length > 0 && (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <Input
              placeholder={t("admin.companies.search_placeholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="sm:max-w-xs"
            />
            <div className="flex flex-wrap gap-1">
              {(["all", "active", "pending_suspension", "suspended"] as StatusFilter[]).map(
                (s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setStatusFilter(s)}
                    className={
                      "whitespace-nowrap rounded-md px-3 py-1.5 text-xs transition-colors " +
                      (statusFilter === s
                        ? "bg-accent-superadmin text-white"
                        : "bg-bg-secondary text-fg-secondary hover:bg-bg-elevated")
                    }
                  >
                    {t(`admin.companies.filter.${s}`)}{" "}
                    <span className="opacity-60">({counts[s]})</span>
                  </button>
                ),
              )}
            </div>
          </div>
        )}

        {companies.isLoading && (
          <p className="text-fg-muted">{t("admin.companies.loading")}</p>
        )}

        {companies.isError && (
          <p className="text-danger">{t("admin.companies.load_error")}</p>
        )}

        {companies.data && companies.data.length === 0 && (
          <EmptyState onCreate={() => setShowCreate(true)} />
        )}

        {companies.data && companies.data.length > 0 && filtered.length === 0 && (
          <div className="rounded-lg border border-border bg-bg-secondary p-8 text-center text-fg-muted">
            {t("admin.companies.empty_filter")}
          </div>
        )}

        {filtered.length > 0 && (
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full whitespace-nowrap text-sm">
              <thead className="bg-bg-secondary text-left text-fg-muted">
                <tr>
                  <th className="px-4 py-3 font-medium w-8" />
                  <th className="px-4 py-3 font-medium">{t("admin.companies.col.name")}</th>
                  <th className="px-4 py-3 font-medium">{t("admin.companies.col.status")}</th>
                  <th className="px-4 py-3 font-medium">{t("admin.companies.col.users")}</th>
                  <th className="px-4 py-3 font-medium">{t("admin.companies.col.created")}</th>
                  <th className="px-4 py-3 font-medium text-right">
                    {t("admin.companies.col.actions")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <CompanyRow
                    key={c.id}
                    company={c}
                    expanded={expandedId === c.id}
                    onToggle={() => setExpandedId(expandedId === c.id ? null : c.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        <HelpSection
          i18nKey="admin.help"
          paragraphs={3}
          list={{
            titleKey: "admin.help.list_title",
            itemsKey: "admin.help.list_items",
            itemCount: 3,
          }}
        />
      </section>

      {showCreate && (
        <CreateCompanyModal
          onClose={() => setShowCreate(false)}
          onCreated={(invite) => {
            setShowCreate(false);
            setInviteResult({ url: invite.invite_url, expires: invite.invite_expires_at });
          }}
        />
      )}

      {inviteResult && (
        <InviteLinkModal
          url={inviteResult.url}
          expiresAt={inviteResult.expires}
          onClose={() => setInviteResult(null)}
        />
      )}
    </main>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-border bg-bg-secondary p-12 text-center space-y-4">
      <p className="text-fg-secondary">{t("admin.companies.empty_title")}</p>
      <Button className="bg-accent-superadmin hover:opacity-90" onClick={onCreate}>
        {t("admin.companies.create_first")}
      </Button>
    </div>
  );
}

function CompanyRow({
  company,
  expanded,
  onToggle,
}: {
  company: CompanySummary;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const unsuspend = useUnsuspendCompany();
  const del = useDeleteCompany();
  const [showSuspend, setShowSuspend] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const statusBadge = {
    active: <span className="text-success">●</span>,
    pending_suspension: <span className="text-warning">●</span>,
    suspended: <span className="text-danger">●</span>,
  }[company.status];

  return (
    <>
      <tr className="border-t border-border hover:bg-bg-secondary/50 cursor-pointer" onClick={onToggle}>
        <td className="px-4 py-3 text-fg-muted">
          <span className="inline-block transition-transform" style={{ transform: expanded ? "rotate(90deg)" : "" }}>
            ▸
          </span>
        </td>
        <td className="px-4 py-3">
          <div className="font-medium">{company.name}</div>
          <div className="text-xs text-fg-muted font-mono">{company.slug}</div>
        </td>
        <td className="px-4 py-3 text-sm">
          {statusBadge} {t(`admin.companies.status.${company.status}`)}
          {company.suspension_scheduled_at && (
            <div className="mt-1 text-xs">
              <span className="text-warning">
                {t("admin.companies.freeze_in", {
                  left: formatCountdown(company.suspension_scheduled_at, t),
                })}
              </span>
              <span className="ml-1 text-fg-muted">
                ({formatDate(company.suspension_scheduled_at)})
              </span>
            </div>
          )}
        </td>
        <td className="px-4 py-3 text-sm">{company.user_count}</td>
        <td className="px-4 py-3 text-sm text-fg-muted">{formatDate(company.created_at)}</td>
        <td
          className="flex flex-wrap items-center justify-end gap-2 px-4 py-3"
          onClick={(e) => e.stopPropagation()}
        >
          <EnterButton company={company} />
          {company.status === "active" ? (
            <Button size="sm" variant="secondary" onClick={() => setShowSuspend(true)}>
              {t("admin.companies.suspend")}
            </Button>
          ) : (
            <>
              {company.status === "pending_suspension" && (
                <Button size="sm" variant="secondary" onClick={() => setShowSuspend(true)}>
                  {t("admin.companies.change_timer")}
                </Button>
              )}
              <Button
                size="sm"
                variant="secondary"
                onClick={() => unsuspend.mutate(company.id)}
                disabled={unsuspend.isPending}
              >
                {t("admin.companies.unsuspend")}
              </Button>
            </>
          )}
          <Button size="sm" variant="danger" onClick={() => setShowDelete(true)}>
            {t("admin.companies.delete")}
          </Button>
        </td>
      </tr>

      {expanded && (
        <tr className="bg-bg-secondary/30">
          <td colSpan={6} className="px-4 py-4">
            <CompanyDetails company={company} />
          </td>
        </tr>
      )}

      {showDelete && (
        <tr>
          <td colSpan={6} className="bg-bg-elevated">
            <DeleteConfirmation
              expectedSlug={company.slug}
              onCancel={() => setShowDelete(false)}
              onConfirm={async () => {
                await del.mutateAsync({ id: company.id, confirmation_slug: company.slug });
                setShowDelete(false);
              }}
              pending={del.isPending}
            />
          </td>
        </tr>
      )}

      {showSuspend && (
        <SuspendModal
          company={company}
          onClose={() => setShowSuspend(false)}
        />
      )}
    </>
  );
}

function CompanyDetails({ company }: { company: CompanySummary }) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
      <div>
        <div className="text-xs text-fg-muted mb-1">{t("admin.companies.detail.slug")}</div>
        <code className="font-mono text-xs">{company.slug}</code>
      </div>
      <div>
        <div className="text-xs text-fg-muted mb-1">{t("admin.companies.detail.id")}</div>
        <code className="font-mono text-xs text-fg-muted break-all">{company.id}</code>
      </div>
      {company.notes && (
        <div className="sm:col-span-2">
          <div className="text-xs text-fg-muted mb-1">{t("admin.companies.detail.notes")}</div>
          <p className="whitespace-pre-wrap text-fg-secondary">{company.notes}</p>
        </div>
      )}
      {!company.notes && (
        <div className="sm:col-span-2 text-fg-muted text-xs italic">
          {t("admin.companies.detail.no_notes")}
        </div>
      )}
    </div>
  );
}

/** "2 дн 4 ч" until the deadline; "просрочен" once it has passed (the enforcer
 * cron runs every 5 minutes, so a few minutes of overshoot is normal). */
function formatCountdown(
  iso: string,
  t: ReturnType<typeof useTranslation>["t"],
): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return t("admin.companies.overdue");
  const minutes = Math.floor(ms / 60000);
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = minutes % 60;
  const parts = days > 0 ? [days + t("admin.companies.unit_d"), hours + t("admin.companies.unit_h")] : hours > 0 ? [hours + t("admin.companies.unit_h"), mins + t("admin.companies.unit_m")] : [mins + t("admin.companies.unit_m")];
  return parts.join(" ");
}

function SuspendModal({
  company,
  onClose,
}: {
  company: CompanySummary;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const suspend = useSuspendCompany();
  const [mode, setMode] = useState<"timer" | "immediate">("timer");
  // Presets cover the normal cases (trial ends, unpaid invoice); "custom" is
  // for a date the operator has agreed with the client.
  const [preset, setPreset] = useState<string>("24");
  const [customAt, setCustomAt] = useState("");
  const [reason, setReason] = useState("");

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (mode === "immediate") {
      await suspend.mutateAsync({
        id: company.id,
        reason: reason || undefined,
        immediate: true,
      });
    } else if (preset === "custom") {
      // datetime-local gives a wall-clock string in the admin's own zone;
      // Date() reads it as local time and toISOString converts to UTC, which
      // is what the API stores.
      await suspend.mutateAsync({
        id: company.id,
        reason: reason || undefined,
        freeze_at: new Date(customAt).toISOString(),
      });
    } else {
      await suspend.mutateAsync({
        id: company.id,
        reason: reason || undefined,
        in_hours: Number(preset),
      });
    }
    onClose();
  };

  const customInvalid =
    mode === "timer" &&
    preset === "custom" &&
    (!customAt || new Date(customAt).getTime() <= Date.now());

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-bg-primary/80 p-4">
      <form
        onSubmit={handleSubmit}
        className="max-h-full w-full max-w-md space-y-4 overflow-y-auto rounded-lg border border-border bg-bg-secondary p-6"
      >
        <h3 className="text-xl font-semibold">
          {t("admin.companies.suspend_title", { name: company.name })}
        </h3>

        <div className="space-y-2">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="radio"
              name="mode"
              checked={mode === "timer"}
              onChange={() => setMode("timer")}
              className="mt-0.5"
            />
            <div className="min-w-0 flex-1">
              <div className="font-medium">{t("admin.companies.mode.timer")}</div>
              <p className="text-xs text-fg-muted">{t("admin.companies.mode.timer_hint")}</p>
            </div>
          </label>

          {mode === "timer" && (
            <div className="ml-7 space-y-2">
              <div className="flex flex-wrap gap-1.5">
                {(
                  [
                    ["1", "1h"],
                    ["24", "24h"],
                    ["72", "3d"],
                    ["168", "7d"],
                    ["720", "30d"],
                    ["custom", "custom"],
                  ] as [string, string][]
                ).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setPreset(value)}
                    className={
                      preset === value
                        ? "rounded-md border border-accent bg-accent/15 px-2.5 py-1 text-xs font-medium text-fg-primary"
                        : "rounded-md border border-border px-2.5 py-1 text-xs text-fg-secondary hover:border-fg-muted"
                    }
                  >
                    {t(`admin.companies.preset.${label}`)}
                  </button>
                ))}
              </div>
              {preset === "custom" && (
                <Input
                  type="datetime-local"
                  aria-label={t("admin.companies.preset.custom")}
                  value={customAt}
                  onChange={(e) => setCustomAt(e.target.value)}
                />
              )}
              <p className="text-xs text-fg-muted">
                {t("admin.companies.timer_invisible_hint")}
              </p>
            </div>
          )}

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="radio"
              name="mode"
              checked={mode === "immediate"}
              onChange={() => setMode("immediate")}
              className="mt-0.5"
            />
            <div>
              <div className="font-medium text-danger">
                {t("admin.companies.mode.immediate")}
              </div>
              <p className="text-xs text-fg-muted">
                {t("admin.companies.mode.immediate_hint")}
              </p>
            </div>
          </label>
        </div>

        <div className="space-y-2">
          <Label htmlFor="suspend_reason">{t("admin.companies.suspend_reason")}</Label>
          <Input
            id="suspend_reason"
            placeholder={t("admin.companies.suspend_reason_placeholder")}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <p className="text-xs text-fg-muted">{t("admin.companies.suspend_reason_hint")}</p>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            variant={mode === "immediate" ? "danger" : "default"}
            disabled={suspend.isPending || customInvalid}
          >
            {suspend.isPending
              ? t("admin.companies.suspending")
              : mode === "immediate"
                ? t("admin.companies.suspend_now")
                : t("admin.companies.arm_timer")}
          </Button>
        </div>
      </form>
    </div>
  );
}

function DeleteConfirmation({
  expectedSlug,
  onCancel,
  onConfirm,
  pending,
}: {
  expectedSlug: string;
  onCancel: () => void;
  onConfirm: () => void;
  pending: boolean;
}) {
  const { t } = useTranslation();
  const [typed, setTyped] = useState("");
  return (
    <div className="px-4 py-4 space-y-3">
      <p className="text-sm text-danger">
        {t("admin.companies.delete_warning", { slug: expectedSlug })}
      </p>
      <div className="flex items-center gap-2">
        <Input
          placeholder={expectedSlug}
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          className="max-w-xs"
        />
        <Button
          variant="danger"
          disabled={typed !== expectedSlug || pending}
          onClick={() => onConfirm()}
        >
          {pending ? t("admin.companies.deleting") : t("admin.companies.delete_confirm")}
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          {t("common.cancel")}
        </Button>
      </div>
    </div>
  );
}

function CreateCompanyModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (invite: { invite_url: string; invite_expires_at: string }) => void;
}) {
  const { t } = useTranslation();
  const create = useCreateCompany();
  const [name, setName] = useState("");
  const [adminUsername, setAdminUsername] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Slug is derived from the name behind the scenes — it's a URL-safe
  // identifier the backend needs but the operator doesn't care about.
  // We show a small preview underneath so it's not magical.
  const generatedSlug = autoSlug(name);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!generatedSlug) {
      setError(t("admin.companies.error_slug_blank"));
      return;
    }
    try {
      const result = await create.mutateAsync({
        name,
        slug: generatedSlug,
        admin_username: adminUsername,
        notes: notes || null,
      });
      onCreated({
        invite_url: result.invite_url,
        invite_expires_at: result.invite_expires_at,
      });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.detail);
      } else {
        setError(t("admin.companies.create_error"));
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-bg-primary/80 p-4">
      <form
        onSubmit={handleSubmit}
        className="max-h-full w-full max-w-md space-y-4 overflow-y-auto rounded-lg border border-border bg-bg-secondary p-6"
      >
        <h3 className="text-xl font-semibold">{t("admin.companies.create_title")}</h3>

        <div className="space-y-2">
          <Label htmlFor="company_name">{t("admin.companies.field.name")}</Label>
          <Input
            id="company_name"
            required
            placeholder={t("admin.companies.field.name_placeholder")}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          {generatedSlug && (
            <p className="text-xs text-fg-muted">
              {t("admin.companies.slug_preview")}{" "}
              <code className="font-mono text-fg-secondary">{generatedSlug}</code>
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="company_admin">{t("admin.companies.field.admin_username")}</Label>
          <Input
            id="company_admin"
            required
            placeholder="alice"
            pattern="[A-Za-z0-9_-]+"
            value={adminUsername}
            onChange={(e) => setAdminUsername(e.target.value)}
          />
          <p className="text-xs text-fg-muted">{t("admin.companies.field.admin_username_hint")}</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="company_notes">{t("admin.companies.field.notes")}</Label>
          <textarea
            id="company_notes"
            rows={3}
            placeholder={t("admin.companies.field.notes_placeholder")}
            className="w-full rounded-md border border-border bg-bg-secondary px-3 py-2 text-sm text-fg-primary placeholder:text-fg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>

        {error && <p className="text-sm text-danger">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            className="bg-accent-superadmin hover:opacity-90"
            disabled={create.isPending}
          >
            {create.isPending ? t("admin.companies.creating") : t("admin.companies.create")}
          </Button>
        </div>
      </form>
    </div>
  );
}

function InviteLinkModal({
  url,
  expiresAt,
  onClose,
}: {
  url: string;
  expiresAt: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const fullUrl = `${window.location.origin}${url}`;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-bg-primary/80 p-4">
      <div className="w-full max-w-lg space-y-4 rounded-lg border border-border bg-bg-secondary p-6">
        <h3 className="text-xl font-semibold">{t("admin.companies.invite_title")}</h3>
        <p className="text-sm text-fg-secondary">{t("admin.companies.invite_subtitle")}</p>

        <code className="block break-all rounded bg-bg-elevated p-3 text-xs font-mono">
          {fullUrl}
        </code>

        <p className="text-xs text-fg-muted">
          {t("admin.companies.invite_expires", { date: formatDate(expiresAt) })}
        </p>

        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            onClick={() => {
              void navigator.clipboard.writeText(fullUrl);
            }}
          >
            {t("admin.companies.invite_copy")}
          </Button>
          <Button onClick={onClose}>{t("common.done")}</Button>
        </div>
      </div>
    </div>
  );
}

// Russian -> Latin transliteration so "Asia Bazar" or "Альфа компания"
// produces a clean URL slug instead of disappearing.
const TRANSLIT: Record<string, string> = {
  а: "a", б: "b", в: "v", г: "g", д: "d", е: "e", ё: "yo", ж: "zh",
  з: "z", и: "i", й: "y", к: "k", л: "l", м: "m", н: "n", о: "o",
  п: "p", р: "r", с: "s", т: "t", у: "u", ф: "f", х: "h", ц: "ts",
  ч: "ch", ш: "sh", щ: "sch", ъ: "", ы: "y", ь: "", э: "e", ю: "yu", я: "ya",
};

function EnterButton({ company }: { company: CompanySummary }) {
  const { t } = useTranslation();
  const enter = useEnterCompany();
  const handle = (e: React.MouseEvent) => {
    e.stopPropagation();
    enter.mutate(company.id, {
      onSuccess: () => {
        // Navigate to the company shell. /me will report impersonating
        // and the App reroutes from CompaniesPage into CompanyShell.
        window.history.pushState(null, "", "/");
        window.dispatchEvent(new PopStateEvent("popstate"));
      },
    });
  };
  return (
    <Button
      size="sm"
      variant="secondary"
      onClick={handle}
      disabled={enter.isPending}
      title={t("admin.companies.enter_hint")}
    >
      {enter.isPending ? t("admin.companies.entering") : t("admin.companies.enter")}
    </Button>
  );
}

function autoSlug(name: string): string {
  const lowered = name.toLowerCase();
  let translit = "";
  for (const ch of lowered) {
    translit += TRANSLIT[ch] ?? ch;
  }
  return translit
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}

function formatDate(iso: string): string {
  return formatDateTime(iso);
}
